"use client";
import { publicUrls } from "@/constants";
import Close24 from "@ui/icons/24x24/close-24.svg";
import clsx from "clsx";
import { useTranslations } from "next-intl";
import { LinkTracked } from "../core/LinkTracked";
import { EventName } from "@mw/shared/analyticsEventNames";
import { captureEvent } from "@/app/_analytics/analytics";
export interface TrackingConfig {
  signUpLink?: {
    eventName: EventName;
    eventProperties?: Record<string, unknown>;
  };
  closeButton?: {
    eventName: EventName;
    eventProperties?: Record<string, unknown>;
  };
}

const BackgroundSVG = () => (
  <svg
    className='absolute top-0 w-full'
    fill='none'
    height='378'
    viewBox='0 0 1090 378'
    width='1090'
    xmlns='http://www.w3.org/2000/svg'
  >
    <g filter='url(#filter0_f_668_524)' opacity='0.4'>
      <rect fill='#406BFC' height='502' rx='200' width='502' x='711' y='-248' />
    </g>
    <g filter='url(#filter1_f_668_524)' opacity='0.4'>
      <rect fill='#8EABEF' height='646' rx='200' width='646' x='138' y='-514' />
    </g>
    <g filter='url(#filter2_f_668_524)' opacity='0.4'>
      <rect fill='#D4F5FA' height='369' rx='184.5' width='369' x='-68' y='-139' />
    </g>
    <defs>
      <filter
        color-interpolation-filters='sRGB'
        filterUnits='userSpaceOnUse'
        height='750'
        id='filter0_f_668_524'
        width='750'
        x='587'
        y='-372'
      >
        <feFlood flood-opacity='0' result='BackgroundImageFix' />
        <feBlend in='SourceGraphic' in2='BackgroundImageFix' mode='normal' result='shape' />
        <feGaussianBlur result='effect1_foregroundBlur_668_524' stdDeviation='62' />
      </filter>
      <filter
        color-interpolation-filters='sRGB'
        filterUnits='userSpaceOnUse'
        height='894'
        id='filter1_f_668_524'
        width='894'
        x='14'
        y='-638'
      >
        <feFlood flood-opacity='0' result='BackgroundImageFix' />
        <feBlend in='SourceGraphic' in2='BackgroundImageFix' mode='normal' result='shape' />
        <feGaussianBlur result='effect1_foregroundBlur_668_524' stdDeviation='62' />
      </filter>
      <filter
        color-interpolation-filters='sRGB'
        filterUnits='userSpaceOnUse'
        height='617'
        id='filter2_f_668_524'
        width='617'
        x='-192'
        y='-263'
      >
        <feFlood flood-opacity='0' result='BackgroundImageFix' />
        <feBlend in='SourceGraphic' in2='BackgroundImageFix' mode='normal' result='shape' />
        <feGaussianBlur result='effect1_foregroundBlur_668_524' stdDeviation='62' />
      </filter>
    </defs>
  </svg>
);

const LimitReachedOverlayControl = ({
  trackingConfig,
  title,
  description,
  rounded = false,
  variant = "default",
  ctaLink = publicUrls.signUp,
  buttonText,
}: {
  description?: string;
  title?: string;
  rounded?: boolean;
  variant?: "default" | "redesigned";
  ctaLink?: string;
  buttonText?: string;
  trackingConfig?: TrackingConfig;
}) => {
  const t = useTranslations("ClientKeys.TTS");
  return (
    <div
      className={clsx(
        "absolute z-30 flex h-full w-full flex-col items-center justify-center p-16 text-center backdrop-blur-xl",
        rounded && variant !== "redesigned" && "rounded-inxl",
        rounded && variant === "redesigned" && "rounded-[24px]",
      )}
    >
      <p className={clsx("mb-8 max-w-lg", variant === "redesigned" ? "f-description-01" : "f-subhead-01")}>
        {title || t("widget.quota_exceeded_title")}
      </p>
      <p className={clsx("mb-24 max-w-lg", variant === "redesigned" ? "f-paragraph-01" : "f-ui-02")}>
        {description || t("widget.quota_exceeded_description")}
      </p>
      <LinkTracked
        asButton
        href={ctaLink}
        buttonVariants={{ size: variant === "redesigned" ? "md" : "lg" }}
        eventName={trackingConfig?.signUpLink?.eventName ?? EventName.mw_limit_reached_overlay_sign_up_clicked}
        eventProperties={trackingConfig?.signUpLink?.eventProperties}
      >
        {buttonText || t("widget.quota_exceeded_button")}
      </LinkTracked>
    </div>
  );
};

export const LimitReachedOverlay = ({
  description,
  title,
  rounded = false,
  variant = "default",
  ctaLink = publicUrls.signUp,
  buttonText,
  onClose,
  trackingConfig = {
    signUpLink: {
      eventName: EventName.mw_limit_reached_overlay_sign_up_clicked,
    },
  },
}: {
  description?: string;
  title?: string;
  rounded?: boolean;
  variant?: "default" | "redesigned";
  ctaLink?: string;
  buttonText?: string;
  onClose?: () => void;
  trackingConfig?: TrackingConfig;
}) => {
  const tGlobals = useTranslations("ClientKeys.Globals");
  return (
    <div
      className={clsx(
        "absolute z-20 flex h-full w-full overflow-hidden",
        rounded && variant !== "redesigned" && "rounded-inxl",
        rounded && variant === "redesigned" && "rounded-[24px]",
      )}
    >
      {variant === "default" && <BackgroundSVG />}
      {onClose && (
        <button
          type='button'
          onClick={() => {
            onClose?.();
            if (trackingConfig?.closeButton) {
              captureEvent(trackingConfig.closeButton.eventName, trackingConfig.closeButton.eventProperties);
            }
          }}
          aria-label={tGlobals("close")}
          className='effect-focus effect-opacity absolute right-20 top-20 z-40 flex h-40 w-40 items-center justify-center rounded-full bg-default text-default hover:opacity-85'
        >
          <Close24 />
        </button>
      )}
      <LimitReachedOverlayControl
        description={description}
        title={title}
        rounded={rounded}
        variant={variant}
        ctaLink={ctaLink}
        buttonText={buttonText}
        trackingConfig={trackingConfig}
      />
    </div>
  );
};
